import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AdvocatePortalPage from "@/pages/AdvocatePortalPage";
import { toast } from "sonner";
import {
  Bell, HeartHandshake, MessageCircle, Send, Upload, Camera,
  ArrowLeft, Filter, Tag, Image, RefreshCw, ExternalLink, AlertCircle, Calendar, X
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const PORTAL_CONFIG = {
  team: { title: "Team Portal", color: "blue", bgClass: "from-blue-600 to-indigo-600" },
  investor: { title: "Investor Portal", color: "emerald", bgClass: "from-emerald-600 to-green-600" },
  network: { title: "Network Portal", color: "purple", bgClass: "from-purple-600 to-pink-600" },
};

// --- Updates Section ---
const PORTAL_CATEGORY_COLORS = {
  hair: { iconColor: "#e83e8c", bgColor: "#ffeef4" },
  makeup: { iconColor: "#9c57f5", bgColor: "#f2e9ff" },
  nails: { iconColor: "#f15a70", bgColor: "#ffe8ec" },
  lashes: { iconColor: "#774cda", bgColor: "#ede7f7" },
  events: { iconColor: "#f9c434", bgColor: "#fff5e6" },
  instructors: { iconColor: "#4a90d9", bgColor: "#e0eeff" },
  culinary: { iconColor: "#f28c28", bgColor: "#fff3e6" },
  wellness: { iconColor: "#75d6d6", bgColor: "#e7f5f5" },
  travel: { iconColor: "#73b7ff", bgColor: "#e7f3ff" },
  dispensary: { iconColor: "#60d680", bgColor: "#e8fef0" },
  retail: { iconColor: "#9950fa", bgColor: "#f0e8ff" },
};

const getUpdateCategoryColor = (tags) => {
  if (!tags || !tags.length) return null;
  for (const tag of tags) {
    if (PORTAL_CATEGORY_COLORS[tag]) return PORTAL_CATEGORY_COLORS[tag];
  }
  return null;
};

const PortalUpdates = ({ portalType }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTag, setFilterTag] = useState("");
  const [selectedUpdate, setSelectedUpdate] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/updates/feed`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          const filtered = (Array.isArray(data) ? data : []).filter(u =>
            (u.is_published !== false) &&
            ((u.assigned_portals || u.target_portals || []).includes(portalType) || (u.target_portals || []).length === 0)
          ).map(u => ({
            ...u,
            cover_image: u.image_url || u.cover_image || (u.media?.[0]?.url),
            tags: u.target_categories || u.tags || [],
          }));
          setUpdates(filtered);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [portalType]);

  const allTags = [...new Set(updates.flatMap(u => u.tags || []))];
  const sorted = [...updates].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const visible = filterTag ? sorted.filter(u => (u.tags || []).includes(filterTag)) : sorted;

  const gridCols = visible.length === 1 ? 'grid-cols-1' : visible.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div data-testid={`portal-updates-${portalType}`}>
      {allTags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <button onClick={() => setFilterTag("")} className={`text-xs px-2.5 py-1 rounded-full transition-colors ${!filterTag ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>All</button>
          {allTags.map(tag => {
            const catColor = PORTAL_CATEGORY_COLORS[tag];
            const isActive = filterTag === tag;
            return (
              <button key={tag} onClick={() => setFilterTag(tag)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors capitalize ${!isActive ? 'bg-gray-100 text-gray-500' : ''}`}
                style={isActive && catColor ? { backgroundColor: catColor.iconColor, color: '#fff' } : isActive ? { backgroundColor: '#111', color: '#fff' } : {}}
              >{tag}</button>
            );
          })}
        </div>
      )}
      {visible.length > 0 ? (
        <div className={`grid ${gridCols} gap-4`}>
          {visible.map(update => {
            const catColor = getUpdateCategoryColor(update.tags);
            const ctaList = update.cta_buttons || update.buttons || [];
            const singleCta = !ctaList.length && update.cta_url ? [{ label: update.cta_text || "Learn More", url: update.cta_url }] : ctaList;
            return (
              <Card
                key={update.update_id}
                className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                style={{
                  borderColor: catColor ? catColor.iconColor + '30' : undefined,
                  borderLeftWidth: catColor ? 3 : 1,
                  borderLeftColor: catColor ? catColor.iconColor : undefined,
                }}
                data-testid={`update-${update.update_id}`}
                onClick={() => setSelectedUpdate(update)}
              >
                {update.cover_image && (
                  <div className="w-full" style={{ aspectRatio: '16/9' }}>
                    <img src={update.cover_image.startsWith('http') ? update.cover_image : `${API}${update.cover_image}`} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="p-4" style={{ backgroundColor: catColor ? catColor.bgColor : undefined }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{update.title}</h4>
                    {update.created_at && (
                      <span className="text-[10px] text-gray-400 shrink-0 flex items-center gap-0.5">
                        <Calendar className="w-2.5 h-2.5" />
                        {!update.hide_date && (new Date(update.display_date || update.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }))}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">{update.content}</p>
                  {singleCta.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {singleCta.map((btn, i) => (
                        <button key={i}
                          className="text-[11px] font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 text-white transition-opacity hover:opacity-90"
                          style={{ backgroundColor: catColor ? catColor.iconColor : '#111' }}
                          onClick={e => { e.stopPropagation(); window.open(btn.url || btn.cta_url, '_blank'); }}
                        >
                          {btn.label || btn.cta_text} <ExternalLink className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Bell className="w-10 h-10 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No updates yet</p>
            <p className="text-gray-400 text-sm mt-1">We'll notify you when there's news</p>
          </CardContent>
        </Card>
      )}

      {/* Update Detail Modal -- white bg, black text */}
      <Dialog open={!!selectedUpdate} onOpenChange={() => setSelectedUpdate(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white" style={{ maxHeight: '85vh' }}>
          <DialogTitle className="sr-only">Update Details</DialogTitle>
          {selectedUpdate && (() => {
            const mc = getUpdateCategoryColor(selectedUpdate.tags);
            const ctaList = selectedUpdate.cta_buttons || selectedUpdate.buttons || [];
            const singleCta = !ctaList.length && selectedUpdate.cta_url ? [{ label: selectedUpdate.cta_text || "Learn More", url: selectedUpdate.cta_url }] : ctaList;
            return (
              <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
                {selectedUpdate.cover_image && (
                  <div className="w-full" style={{ aspectRatio: '16/9' }}>
                    <img src={selectedUpdate.cover_image.startsWith('http') ? selectedUpdate.cover_image : `${API}${selectedUpdate.cover_image}`} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-xl font-bold text-gray-900">{selectedUpdate.title}</h2>
                    {selectedUpdate.created_at && (
                      <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                        <Calendar className="w-3.5 h-3.5" />
                        {!selectedUpdate.hide_date && new Date(selectedUpdate.display_date || selectedUpdate.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedUpdate.content}</p>
                  {(selectedUpdate.gallery_images || []).length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {selectedUpdate.gallery_images.map((img, i) => (
                        <div key={i} className="aspect-video rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                          <img src={typeof img === "string" ? img : (img.url || img.image_url)} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                  {singleCta.length > 0 && (
                    <div className="flex flex-wrap gap-3 pt-2">
                      {singleCta.map((btn, i) => (
                        <Button key={i} className="text-white" style={{ backgroundColor: mc ? mc.iconColor : '#111' }}
                          onClick={() => window.open(btn.url || btn.cta_url, '_blank')}
                        >
                          {btn.label || btn.cta_text} <ExternalLink className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Support/Chat Section (feedback + bug reports) ---
const PortalSupport = ({ portalType, user }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [newMessage, setNewMessage] = useState("");
  const [messageType, setMessageType] = useState("general");
  const [screenshot, setScreenshot] = useState(null);
  const [sending, setSending] = useState(false);

  const loadMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/feedback/my-messages?portal_type=${portalType}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [portalType]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const handleSubmit = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("type", messageType);
      formData.append("message", newMessage);
      formData.append("portal_type", portalType);
      formData.append("page_url", window.location.href);
      if (screenshot) formData.append("attachment", screenshot);

      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/feedback`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (res.ok) {
        toast.success("Message sent!");
        setNewMessage("");
        setScreenshot(null);
        loadMessages();
      } else {
        toast.error("Failed to send message");
      }
    } catch (err) {
      toast.error("Failed to send message");
    }
    setSending(false);
  };

  const filtered = category === "all" ? messages : messages.filter(m => m.type === category);

  return (
    <div className="space-y-4" data-testid={`portal-support-${portalType}`}>
      {/* Message Compose */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> Send a Message
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Select value={messageType} onValueChange={setMessageType}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="bug">Bug Report</SelectItem>
                <SelectItem value="pilot">Pilot Feedback</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="min-h-[60px]"
                data-testid="support-message-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="screenshot-upload" className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded border border-dashed hover:border-gray-400">
                <Camera className="w-3.5 h-3.5" />
                {screenshot ? screenshot.name : "Attach screenshot"}
              </Label>
              <input id="screenshot-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setScreenshot(e.target.files?.[0])} />
            </div>
            <Button size="sm" onClick={handleSubmit} disabled={sending || !newMessage.trim()} data-testid="send-support-message-btn">
              <Send className="w-3.5 h-3.5 mr-1" />
              {sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-1.5">
        {["all", "general", "support", "bug", "pilot"].map(cat => (
          <button key={cat} onClick={() => setCategory(cat)} className={`text-xs px-3 py-1 rounded-full capitalize ${category === cat ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`} data-testid={`filter-${cat}`}>
            {cat}
          </button>
        ))}
      </div>

      {/* Messages */}
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map(msg => (
            <Card key={msg.feedback_id} className={msg.admin_reply ? "border-l-4 border-l-emerald-500" : ""}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{msg.type || "general"}</Badge>
                      <Badge variant={msg.status === "resolved" ? "default" : "secondary"} className="text-[10px]">{msg.status || "open"}</Badge>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">{msg.message}</p>
                    {msg.attachment_url && (
                      <img src={msg.attachment_url.startsWith('http') ? msg.attachment_url : `${API}${msg.attachment_url}`} alt="attachment" className="mt-2 rounded-lg max-h-32 border" />
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 shrink-0">{msg.created_at ? new Date(msg.created_at).toLocaleDateString() : ''}</span>
                </div>
                {msg.admin_reply && (
                  <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <p className="text-xs text-emerald-700 font-medium mb-1">Admin Reply</p>
                    <p className="text-sm text-gray-700">{msg.admin_reply}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-gray-400">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs mt-1">Send a message and we'll get back to you</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Main Portal Page ---
export default function PortalPage() {
  const { portalType } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdvocate, setIsAdvocate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("updates");
  const [hasAccess, setHasAccess] = useState(false);

  const config = PORTAL_CONFIG[portalType] || PORTAL_CONFIG.team;

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, advRes, accessRes] = await Promise.all([
          fetch(`${API}/api/auth/me`, { headers: getHeaders() }),
          fetch(`${API}/api/user/advocate-status`, { headers: getHeaders() }),
          fetch(`${API}/api/user/portal-access`, { headers: getHeaders() }),
        ]);

        if (meRes.ok) {
          const userData = await meRes.json();
          setUser(userData);
          // Super admins always have access
          if (userData.is_super_admin) setHasAccess(true);
        } else {
          navigate("/login");
          return;
        }

        if (advRes.ok) {
          const advData = await advRes.json();
          setIsAdvocate(advData.is_advocate || false);
        }

        if (accessRes.ok) {
          const accessData = await accessRes.json();
          const portals = accessData.portals || [];
          if (portals.includes(portalType)) setHasAccess(true);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [portalType, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Required</h2>
            <p className="text-gray-500 mb-4">You don't have access to the {config.title}. Contact an admin to get added.</p>
            <Button onClick={() => navigate("/me")} variant="outline">Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: "updates", label: "Updates", icon: Bell },
    ...(isAdvocate ? [{ id: "advocate", label: "Advocate", icon: HeartHandshake }] : []),
    { id: "support", label: "Support", icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`portal-page-${portalType}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${config.bgClass} text-white`}>
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate("/me")}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </div>
            {user && (
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user.name}</p>
                <p className="text-xs text-white/60">{user.email}</p>
              </div>
            )}
          </div>
          <h1 className="text-2xl font-bold mt-4">{config.title}</h1>
          <p className="text-white/70 text-sm mt-1">Stay updated with the latest news and connect with the team</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border mb-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="gap-2" data-testid={`portal-tab-${tab.id}`}>
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="updates">
            <PortalUpdates portalType={portalType} />
          </TabsContent>

          {isAdvocate && (
            <TabsContent value="advocate">
              <AdvocatePortalPage embedded />
            </TabsContent>
          )}

          <TabsContent value="support">
            <PortalSupport portalType={portalType} user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
